import { type DemandeStore } from '@/features/interface/demande/shared'
import type { ModelDemande } from '@/features/domaine/types/opco'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import type { ActionFormationDTO } from '@/features/api/extranet/dto/Demande/AF'
import type { ContratProDTO } from '@/features/api/extranet/dto/Demande/CPRO'
import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'

export type ModeleDemandeBornés = Extract<ModelDemande, 'MODEL_CPRO' | 'MODEL_AF' | 'MODEL_CXPI'>

const NB_MAX_SALARIES = 30 as const
const NB_MAX_SALARIES_PLUS_10_MODULES = 10 as const
export const NB_MAX_MODULES = 10 as const
const NB_MAX_MODULES_PLUS_10_SALARIES = 5 as const

export function useNombreMaxSalariesModules(store: DemandeStore<ModeleDemandeBornés, any>) {
  const { model } = storeToRefs(store)

  const typeModele = computed(() => {
    if (!model.value) throw new Error('Store non initialisé')

    switch (model.value!.dispositif) {
      case 'CPRO':
        return 'MODEL_CPRO'
      case 'AF':
        return 'MODEL_AF'
      case 'FPE': {
        if (model.value!.sousDispositif === 'AfFpe') return 'MODEL_AF'
        else return 'MODEL_CXPI'
      }
      default: {
        const _demande: never = model.value
        throw new Error(`Modèle inattendu`)
      }
    }
  })

  const compteurs = computed(() => {
    switch (typeModele.value) {
      case 'MODEL_CPRO': {
        const demande = model.value as ContratProDTO
        return {
          nbSalaries: 1,
          nbModules: demande.formation?.modules?.length ?? 0,
        }
      }
      case 'MODEL_AF': {
        const demande = model.value as ActionFormationDTO
        return {
          nbSalaries: demande.salaries?.length ?? 0,
          nbModules: demande.formation?.modules?.length ?? 0,
        }
      }
      case 'MODEL_CXPI': {
        const demande = model.value as ContratCxpiDTO
        return {
          nbSalaries: 1,
          nbModules: demande.formation?.modules?.length ?? 0,
        }
      }
      default: {
        const _modele: never = typeModele.value
        throw new Error(`Modèle inattendu`)
      }
    }
  })

  const compteursMax = computed(() => {
    switch (typeModele.value) {
      case 'MODEL_CPRO':
      case 'MODEL_CXPI': {
        return {
          nbMaxSalaries: 1,
          nbMaxModules: NB_MAX_MODULES,
        }
      }
      case 'MODEL_AF': {
        return {
          nbMaxSalaries:
            compteurs.value.nbModules > NB_MAX_MODULES_PLUS_10_SALARIES
              ? NB_MAX_SALARIES_PLUS_10_MODULES
              : NB_MAX_SALARIES,
          nbMaxModules:
            compteurs.value.nbSalaries > NB_MAX_SALARIES_PLUS_10_MODULES
              ? NB_MAX_MODULES_PLUS_10_SALARIES
              : NB_MAX_MODULES,
        }
      }
      default: {
        const _modele: never = typeModele.value
        throw new Error(`Modèle inattendu`)
      }
    }
  })

  const nbMaxSalariesMessage = computed(() => {
    if (compteurs.value.nbSalaries < compteursMax.value.nbMaxSalaries)
      return undefined
    if (compteursMax.value.nbMaxSalaries === NB_MAX_SALARIES_PLUS_10_MODULES) {
      return `Vous ne pouvez pas ajouter plus de ${NB_MAX_SALARIES_PLUS_10_MODULES} salaries sur une demande qui comportent plus de ${NB_MAX_MODULES_PLUS_10_SALARIES} modules de formation.`
    } else {
      return `Vous avez atteint le nombre maximum de salariés autorisés (${NB_MAX_SALARIES}).`
    }
  })

  const nbMaxModulesMessage = computed(() => {
    if (compteurs.value.nbModules < compteursMax.value.nbMaxModules)
      return undefined
    if (compteursMax.value.nbMaxModules === NB_MAX_MODULES_PLUS_10_SALARIES) {
      return `Vous ne pouvez pas ajouter plus de ${NB_MAX_MODULES_PLUS_10_SALARIES} modules sur une demande qui comportent plus de ${NB_MAX_SALARIES_PLUS_10_MODULES} salaries.`
    } else {
      return `Vous avez atteint le nombre maximum de modules autorisés (${NB_MAX_MODULES}).`
    }
  })

  return { nbMaxSalariesMessage, nbMaxModulesMessage, compteursMax }
}
